export interface EmailChallenge {
  challengeId: string;
  channel: 'email';
  expiresInSeconds: number;
  resendAfterSeconds: number;
}

export interface AccountIdentity {
  dashboardPages?: string[];
  dashboardReadOnly?: boolean;
  id: string;
  email: string;
  phone: string;
  email_verified_at: string;
  memberships: { organization_id: string; role: string }[];
  platformRoles: string[];
  onboarding: 'company_details_required';
}

export interface SignupInput {
  email: string;
  phone: string;
  password: string;
  acceptTerms: boolean;
  termsVersion: string;
  privacyVersion: string;
  termsDocumentId: string;
  privacyDocumentId: string;
}

export const MINIMUM_PASSWORD_LENGTH = 15;

export class AuthApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly challenge?: EmailChallenge;

  constructor(status: number, code: string, message: string, challenge?: EmailChallenge) {
    super(message);
    this.name = 'AuthApiError';
    this.status = status;
    this.code = code;
    this.challenge = challenge;
  }
}

// Same-origin requests keep HttpOnly cookies out of JavaScript and browser storage.
async function request<Result>(path: string, body?: unknown): Promise<Result> {
  const response = await fetch(`/api/v1/auth/${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    credentials: 'include',
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (response.status === 204) return undefined as Result;
  const payload = await response.json().catch(() => null) as {
    data?: Result; error?: { code: string; message: string; details?: EmailChallenge };
  } | null;
  if (!response.ok) {
    throw new AuthApiError(response.status, payload?.error?.code ?? 'API_UNAVAILABLE',
      payload?.error?.message ?? 'The API is unavailable. Check that the backend is running.', payload?.error?.details);
  }
  if (!payload || !('data' in payload)) throw new Error('Unexpected authentication response.');
  return payload.data as Result;
}

export const signup = (input: SignupInput) => request<EmailChallenge>('signup', input);
export const demoStatus = () => request<{ enabled: boolean }>('demo/status');
export const demoSignin = (role: string) => request<AccountIdentity>('demo/signin', { role });
export const signin = (email: string, password: string) => request<AccountIdentity | EmailChallenge>('signin', { email, password });
export const resendCode = (challengeId: string) => request<EmailChallenge>('resend-code', { challengeId });
export const verifyEmail = (challengeId: string, code: string) => request<AccountIdentity>('verify-email', { challengeId, code });
export const currentAccount = () => request<AccountIdentity>('me');
export const signout = () => request<void>('signout', {});
export interface SocialProviders { google: { clientId: string } | null; telegram: { clientId: string } | null }
export const socialProviders = () => request<SocialProviders>('providers');
export const telegramNonce = () => request<{ nonce: string }>('telegram/nonce');
// Returned instead of an account when this Google or Telegram identity has none yet.
export interface SocialSignupOffer { signupRequired: true; provider: 'google' | 'telegram'; signupToken: string; email?: string; phone?: string }
export const googleSignin = (code: string) => request<AccountIdentity | SocialSignupOffer>('google', { code });
export const telegramSignin = (idToken: string) => request<AccountIdentity | SocialSignupOffer>('telegram', { idToken });
export type SocialSignupInput = Omit<SignupInput, 'email' | 'password'> & { signupToken: string; email?: string };
export const socialSignup = (input: SocialSignupInput) => request<AccountIdentity | EmailChallenge>('social-signup', input);
export const linkTelegram = (idToken: string) => request<void>('telegram/link', { idToken });
export const connectedProviders = () => request<('google' | 'telegram')[]>('identities');
export const requestSigninCode = (email: string) => request<EmailChallenge>('request-signin-code', { email });
export const requestPasswordReset = (email: string) => request<EmailChallenge>('request-password-reset', { email });
export const resetPassword = (challengeId: string, code: string, password: string) =>
  request<void>('reset-password', { challengeId, code, password });

export function normalizePhone(countryCode: string, nationalNumber: string): string {
  const digits = nationalNumber.replace(/[\s()-]/g, '');
  // Browser autofill often supplies the full international number, code included.
  if (digits.startsWith('+')) return digits;
  return countryCode + digits.replace(/^0+/, '');
}
